import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import "../../Dashboard/Darts/masterConfiguration/productConfiguration/productConfiguration.css";
import Edit from "../../../Images/Edit.png";
import Delete from "../../../Images/deleteButton-icon.png";
import LeadIndicatorConfiguration from "../../Dashboard/Darts/masterConfiguration/productConfiguration/leadIndicatorConfiguration";
import { TargetFunnel, Get_TargetFunnel } from "../../../services/userAPI";
import toggleImage02 from "../../../Images/toggles-fill-image02.png";
import { message } from "antd";
import { useLocation, useNavigate } from "react-router-dom";

const AdminTargetFunnelConfiguration = ({ onBack, onNext, roleId, accountId }) => {
    const location = useLocation();
    const navigate = useNavigate();

    const [isTargetCollapsed, setIsTargetCollapsed] = useState(false);
    const [isAddingNewTarget, setIsAddingNewTarget] = useState(false);
    const [targets, setTargets] = useState([]);
    const [leadIndicators, setLeadIndicators] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);

    const [targetName, setTargetName] = useState("");
    const [monthlyTarget, setMonthlyTarget] = useState("");
    const [color, setColor] = useState("#add8e6");
    const [errors, setErrors] = useState({});

    const steps = ["Step 1", "Step 2", "Step 3", "Step 4"];
    const [currentStep, setCurrentStep] = useState(0);

    const name = location.state?.name;
    const accountName = location.state?.accountName;

    const [saveKpiMasterDetails, { loading, error }] = useMutation(TargetFunnel);

    const { data, loading: getTargetFunnel, error: getErrorTargetFunnel, refetch } = useQuery(Get_TargetFunnel, {
        variables: { roleId: roleId },
        skip: !roleId, // Prevents fetching if roleId is not available
    });

    // Format color for display (receiving from API)
    const formatColorForDisplay = (colorValue) => {
        if (!colorValue) return "#add8e6"; // Default color

        let colorCode = colorValue;

        // Remove "FF" if it starts with it
        if (colorCode.toLowerCase().startsWith("ff")) {
            colorCode = colorCode.slice(2);
        }

        // Ensure the color starts with "#"
        if (!colorCode.startsWith("#")) {
            colorCode = `#${colorCode}`;
        }

        return colorCode;
    };

    // Format color for API (sending to API)
    const formatColorForAPI = (colorValue) => {
        if (!colorValue) return "add8e6"; // Default color without #

        // Remove # if present
        let colorCode = colorValue.startsWith("#") ? colorValue.slice(1) : colorValue;

        // Do not add FF prefix when sending to API
        return colorCode;
    };

    useEffect(() => {
        if (data?.getKpisForRole) {
            setTargets(
                data.getKpisForRole.map(kpi => {
                    return {
                        id: kpi.id,
                        name: kpi.name,
                        monthly: kpi.defaultValue,
                        color: formatColorForDisplay(kpi.colorValue),
                    };
                })
            );
        }
    }, [data]);

    // Handler for receiving lead indicators from child component
    const handleLeadIndicatorsChange = (indicators) => {
        setLeadIndicators(indicators);
    };

    const validate = () => {
        let newErrors = {};
        if (!targetName.trim()) newErrors.targetName = "Target Funnel Name is required.";
        if (!monthlyTarget || isNaN(monthlyTarget) || Number(monthlyTarget) <= 0) {
            newErrors.monthlyTarget = "Monthly Target must be a positive number.";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleAddOrUpdate = async () => {
        if (validate()) {
            try {
                const isEditing = editingIndex !== null;
                const selectedTarget = isEditing ? targets[editingIndex] : null;

                // Format color properly for API
                const brandColor = formatColorForAPI(color);

                const response = await saveKpiMasterDetails({
                    variables: {
                        accountId: accountId,
                        roleId: roleId,
                        kpiId: isEditing ? selectedTarget.id : null, // Send existing kpiId if editing
                        kpiName: targetName,
                        kpiDefaultTarget: Number(monthlyTarget),
                        kpiColorCode: brandColor, // HEX color format without #
                        status: 1 // Active status
                    },
                });

                if (response.data.saveKpiMasterDetails.success) {
                    // After successful API call, update local state properly
                    const newTargetData = response.data.saveKpiMasterDetails.data;
                    const newTarget = {
                        id: newTargetData.id,
                        name: newTargetData.name,
                        monthly: newTargetData.defaultValue,
                        color: formatColorForDisplay(newTargetData.colorValue),
                    };

                    let updatedTargets;
                    if (isEditing) {
                        // Update existing target
                        updatedTargets = [...targets];
                        updatedTargets[editingIndex] = newTarget;
                    } else {
                        // Add new target
                        updatedTargets = [...targets, newTarget];
                    }

                    // Refresh the data
                    await refetch();

                    // Reset form state
                    setTargets(updatedTargets);
                    setTargetName("");
                    setMonthlyTarget("");
                    setColor("#add8e6");
                    setIsAddingNewTarget(false);
                    setEditingIndex(null);
                } else {
                    message.error(response.data.saveKpiMasterDetails.message || "Failed to save target funnel.");
                }
            } catch (err) {
                console.error("API Error:", err);
            }
        }
    };

    const handleEdit = (index) => {
        const target = targets[index];
        setTargetName(target.name);
        setMonthlyTarget(target.monthly);
        setColor(target.color);
        setEditingIndex(index);
        setIsAddingNewTarget(true);
    };

    const handleDelete = async (index) => {
        const targetToDelete = targets[index];

        try {
            // Format color properly for API
            const colorCode = formatColorForAPI(targetToDelete.color);

            const response = await saveKpiMasterDetails({
                variables: {
                    accountId: accountId,
                    roleId: roleId,
                    kpiId: targetToDelete.id,
                    kpiName: targetToDelete.name,
                    kpiDefaultTarget: targetToDelete.monthly,
                    kpiColorCode: colorCode,
                    status: 0 // Set status to 0 for deletion
                },
            });

            if (response.data.saveKpiMasterDetails.success) {
                // Update local state after deletion
                const updatedTargets = targets.filter((_, i) => i !== index);
                setTargets(updatedTargets);

                // Refresh the data
                await refetch();
            } else {
                message.error(response.data.saveKpiMasterDetails.message || "Failed to delete target funnel.");
            }
        } catch (err) {
            console.error("API Error:", err);
        }
    };

    const handleNext = () => {
        // Validate both target funnels and lead indicators
        if (!targets.length) {
            message.error("Please configure at least one Target Funnel before proceeding.");
            return;
        }

        if (!leadIndicators.length) {
            message.error("Please configure at least one Lead Indicator before proceeding.");
            return;
        }

        // Proceed to the next step if applicable
        if (currentStep < steps.length - 1) {
            setCurrentStep(prevStep => prevStep + 1);
        }

        // Pass data to preview component through parent's onNext handler
        onNext({
            name: targetName,
            monthly: monthlyTarget,
            targets: targets,
            leadIndicators: leadIndicators
        });
    };


    const [tooltipText, setTooltipText] = useState("");
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

    const handleMouseEnter = (e, text) => {
        setTooltipText(text);
        setTooltipPosition({ x: e.pageX, y: e.pageY });
        setShowTooltip(true);
    };

    const handleMouseLeave = () => {
        setShowTooltip(false);
    };

    const handleBack = () => {
        navigate("/admin-home"); // This will navigate back to the previous page
    }
    const handleDiscardClick = () => {
        navigate("/admin-home");
    };

    const dartsFormNeeded = location.state?.dartsFormNeeded;
    const pocName = location.state?.pocName;
    const pocEmail = location.state?.pocEmail;
    const pocNumber = location.state?.pocNumber;
    const numberLogin = location.state?.numberLogin;
    const emailLogin = location.state?.emailLogin;
    const mandatoryTimeDistribution = location.state?.mandatoryTimeDistribution;
    const targetEditDays = location.state?.targetEditDays;
    const inputEditDays = location.state?.inputEditDays;
    const dartsNeeded = location.state?.dartsNeeded;
    const clanNeeded = location.state?.clanNeeded;
    const syncNeeded = location.state?.syncNeeded;
    // const roleId = location.state?.roleId;
    // const accountId = location.state?.accountId;

    const [inputType, setInputType] = useState(null);


    const handleBackToViewRole = () => {
        navigate("/admin-view-role", {
            state: {
                roleId: roleId || location.state?.roleId,
                accountId: accountId || location.state?.accountId,
                accountName: accountName || location.state?.accountName,
                name: name || location.state?.name,
                dartsNeeded: inputType ? true : dartsNeeded || location.state?.dartsNeeded, // Update based on inputType
                clanNeeded: clanNeeded || location.state?.clanNeeded,
                syncNeeded: syncNeeded || location.state?.syncNeeded,
                dartsFormNeeded: inputType === "Forms" || dartsFormNeeded || location.state?.dartsFormNeeded,
                pocName: pocName || location.state?.pocName,
                pocEmail: pocEmail || location.state?.pocEmail,
                pocNumber: pocNumber || location.state?.pocNumber,
                numberLogin: numberLogin || location.state?.numberLogin,
                emailLogin: emailLogin || location.state?.emailLogin,
                mandatoryTimeDistribution: mandatoryTimeDistribution || location.state?.mandatoryTimeDistribution,
                targetEditDays: targetEditDays || location.state?.targetEditDays,
                inputEditDays: inputEditDays || location.state?.inputEditDays,
            },
        });
    };
        return (
            <div className="user-management-container">
                < div className="user-management-header" >
                    <div className="user-management-breadcrumb">
                        <span
                            onClick={handleBack}
                            style={{ fontFamily: "Inter", cursor: "pointer", }}
                        >
                            {accountName}
                        </span>

                        <span>&gt;</span>

                        <span
                            onClick={handleBackToViewRole}
                            style={{ fontFamily: "Inter", cursor: "pointer", }}
                        >
                            {name}
                        </span>
                        <span>&gt;</span>
                        <span style={{ fontWeight: "bold" }} className="bold-text">Darts</span>
                    </div>


                    <button className="user-management-back-button" onClick={handleDiscardClick}>
                        {/* <img src={LeftArrow} alt="Less Than" style={{ marginRight: "5px", height: "14px", width: "14px" }} /> */}
                        Back to home
                        {/* <img src={HomeIcon} alt="Less Than" style={{ marginLeft: "5px", height: "13px", width: "13px" }} /> */}

                    </button>
                </div >
                <div className="accordion">
                    <img
                        src={toggleImage02}
                        className="centered-image"
                        alt="Toggle Image"
                    />
                    <div className="accordion-item">
                        <button className="accordion-header" onClick={() => setIsTargetCollapsed(!isTargetCollapsed)}>
                            <span>Target Funnel Configuration</span>
                            <span className="arrow-icon">{isTargetCollapsed ? "▼" : "▲"}</span>
                        </button>

                        {!isTargetCollapsed && (
                            <div className="accordion-content">
                                <div className="target-container">
                                    {targets.map((target, index) => (
                                        editingIndex === index ? (
                                            // Show edit form in place of the card
                                            <div key={index} className="target-box">
                                                <input
                                                    type="text"
                                                    placeholder="Target Funnel Name"
                                                    className="target-input"
                                                    value={targetName}
                                                    onChange={(e) => setTargetName(e.target.value)}
                                                />
                                                {errors.targetName && <span className="error">{errors.targetName}</span>}

                                                <div className="target-row">
                                                    <div className="color-picker">
                                                        <label>Color</label>
                                                        <input
                                                            type="color"
                                                            className="color-input"
                                                            value={color}
                                                            onChange={(e) => setColor(e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="monthly-target">
                                                        <label>Monthly Target</label>
                                                        <input
                                                            type="number"
                                                            placeholder="000"
                                                            className="monthly-input"
                                                            value={monthlyTarget}
                                                            onChange={(e) => setMonthlyTarget(e.target.value)}
                                                        />
                                                        {errors.monthlyTarget && <span className="error">{errors.monthlyTarget}</span>}
                                                    </div>
                                                </div>

                                                <div className="target-actions">
                                                    <button className="target-delete-btn" onClick={() => {
                                                        setIsAddingNewTarget(false);
                                                        setEditingIndex(null);
                                                        setTargetName("");
                                                        setMonthlyTarget("");
                                                        setColor("#add8e6");
                                                        setErrors({});
                                                    }}>Discard</button>
                                                    <button
                                                        className={`target-add-btn ${targetName && monthlyTarget ? "active" : ""}`}
                                                        onClick={handleAddOrUpdate}
                                                        disabled={!targetName || !monthlyTarget || loading}
                                                    >
                                                        {loading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : "Save"}
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            // Show card if not in edit mode
                                            <div key={index} className="target-card">
                                                <div className="target-header" onMouseEnter={(e) => handleMouseEnter(e, target.name)}
                                                    onMouseLeave={handleMouseLeave}>
                                                    <span title={target.name}>{target.name}</span> {/* Add title attribute for tooltip */}
                                                    <div className="color-circle" style={{ backgroundColor: target.color }}></div>
                                                </div>
                                                <div className="target-body">
                                                    <div className="monthly-label">
                                                        <span>Monthly Target</span>
                                                    </div>
                                                    <strong className="monthly-value">{target.monthly}</strong>
                                                </div>
                                                <div className="target-actions">
                                                    <img className="target-funnel-edit" src={Edit} onClick={() => handleEdit(index)} alt="Edit" />
                                                    <img className="target-funnel-delete" src={Delete} onClick={() => handleDelete(index)} alt="Delete" />
                                                </div>
                                            </div>
                                        )
                                    ))}

                                    {isAddingNewTarget && editingIndex === null && (
                                        <div className="target-box">
                                            <input
                                                type="text"
                                                placeholder="Target Funnel Name"
                                                className="target-input"
                                                value={targetName}
                                                onChange={(e) => setTargetName(e.target.value)}
                                            />
                                            {errors.targetName && <span className="error">{errors.targetName}</span>}

                                            <div className="target-row">
                                                <div className="color-picker">
                                                    <label>Color</label>
                                                    <input
                                                        type="color"
                                                        className="color-input"
                                                        value={color}
                                                        onChange={(e) => setColor(e.target.value)}
                                                    />
                                                </div>
                                                <div className="monthly-target">
                                                    <label>Monthly Target</label>
                                                    <input
                                                        type="number"
                                                        placeholder="000"
                                                        className="monthly-input"
                                                        value={monthlyTarget}
                                                        onChange={(e) => setMonthlyTarget(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                handleAddOrUpdate();
                                                            }
                                                        }}
                                                    />
                                                    {errors.monthlyTarget && <span className="error">{errors.monthlyTarget}</span>}
                                                </div>
                                            </div>

                                            <div className="target-actions">
                                                <button className="target-delete-btn" onClick={() => {
                                                    setIsAddingNewTarget(false);
                                                    setEditingIndex(null);
                                                    setTargetName("");
                                                    setMonthlyTarget("");
                                                    setColor("#add8e6");
                                                    setErrors({});
                                                }}>Discard</button>
                                                <button
                                                    className={`target-add-btn-add-button ${targetName && monthlyTarget ? "active" : ""}`}
                                                    onClick={handleAddOrUpdate}
                                                    disabled={!targetName || !monthlyTarget || loading}
                                                >
                                                    {loading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : "Add"}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {!isAddingNewTarget && editingIndex === null && (
                                        <button
                                            className={` ${targets.length === 0 ? "new-target-btn" : "add-btn"}`}
                                            onClick={() => setIsAddingNewTarget(true)}
                                        >
                                            {targets.length === 0 ? "New Target Funnel" : "Add"}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                        <p style={{ marginLeft: "2%", fontSize: "12px", color: "#888", fontFamily: "Inter", }}>The items displayed from left to right are arranged vertically from bottom to top within the funnel.</p>
                    </div>

                    <LeadIndicatorConfiguration
                        roleId={roleId}
                        accountId={accountId}
                        onIndicatorsChange={handleLeadIndicatorsChange}
                    />
                </div>

                <div className="target-funnel-button-group">
                    <button className="back-btn" onClick={onBack}>Back</button>
                    <button
                        className="next-btn"
                        onClick={handleNext}
                        disabled={currentStep === steps.length - 1}
                    >
                        Next
                    </button>
                </div>
            </div>
        );
    };

    export default AdminTargetFunnelConfiguration;